import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { supabase } from "@/integrations/supabase/client";
import {
  uploadFileToStorage,
  MAX_UPLOAD_BYTES,
  FILE_TOO_LARGE_MESSAGE,
} from "@/lib/resumableUpload";
import { useAuth } from "@/contexts/AuthContext";
import { useMemberUna } from "@/hooks/useMemberUna";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, ArrowRight, ArrowLeft, ShieldAlert, Upload } from "lucide-react";
import { findBannedPurposeTerm } from "@/lib/llcFormationBannedTerms";
import { getDocumentSignedUrl } from "@/lib/documentDownload";

type Formation = {
  id: string;
  owner_user_id: string;
  una_id: string | null;
  stage: number;
  ownership_fork: "una_owned" | "second_tier";
  close_llc: boolean;
  entity_name: string | null;
  purpose_text: string | null;
  form_fields: Record<string, any>;
  wy_filing_id: string | null;
  wy_filing_date: string | null;
  llc_ein: string | null;
  cp575_storage_path: string | null;
  timing_attested: boolean;
  timing_attested_at: string | null;
  rp_role: "manager_default" | "designated_third_party";
  has_third_party_designee: boolean;
  wave_one_generated_at: string | null;
  wave_two_generated_at: string | null;
  not_proceeded: boolean;
};

type FormationDoc = {
  id: string;
  document_kind: string | null;
  storage_path: string;
  wave: number;
  generation_version: number;
  generated_at: string;
  executed_scan_storage_path?: string | null;
  executed_scan_uploaded_at?: string | null;
};

const STAGES = [
  { n: 1, label: "Intent and authorization" },
  { n: 2, label: "Wyoming filing prep" },
  { n: 3, label: "Filing confirmation" },
  { n: 4, label: "EIN" },
  { n: 5, label: "Governance package" },
  { n: 6, label: "Banking package" },
  { n: 7, label: "Officer" },
  { n: 8, label: "Record and verify" },
];

// True when Stage 7 is REQUIRED (a second individual exists anywhere in the formation).
function stage7Required(formation: Formation): boolean {
  const ff = formation.form_fields || {};
  const hasCp2 = !!(ff.cp2_name && String(ff.cp2_name).trim());
  const thirdParty = formation.rp_role === "designated_third_party" && !!ff.rp_name;
  const secondAuthorizer = !!(ff.second_authorizer_name && String(ff.second_authorizer_name).trim())
    && String(ff.second_authorizer_name).trim() !== String(ff.manager_name || "").trim();
  return hasCp2 || thirdParty || secondAuthorizer;
}

// Wave 2 template IDs — used to build the dynamic remaining-details form.
const WAVE2_TEMPLATE_IDS = [
  "f79c0ec7-d7d2-48ae-b21d-6c77c575bfdc", // Full OA
  "df625f5b-f701-452b-ba4c-71d9b815b02e", // Short OA
  "2a6fb6bd-5303-488b-91f6-5452e101ca3c", // Appointment of Manager
  "329f6560-4547-40b9-b04c-a514644a75b7", // Manager's Cert of RP
  "fe754908-a990-47ff-8f53-da1ee6cea73f", // Appointment of RP (conditional)
  "0a780e98-f28b-4acd-9ca6-68a1078dc53a", // Banking Resolution
  "c58079c1-ddf5-4665-b3e3-ca61c940b997", // Beneficial Ownership
  "6f78c7f4-a5d1-4bf6-9fe3-447847dd3ede", // Cert of Org O&A
  "b2c3d4e5-1111-4b11-8111-000000001011", // Cert of Incumbency
  "a1b2c3d4-1010-4a10-8010-000000001010", // Source of Funds
];

// Fields already captured in earlier stages, in formation columns, or in
// the steward profile. These are excluded from the dynamic Stage 5 form.
const CAPTURED_FIELDS = new Set([
  "una_name", "una_address", "llc_name", "wy_filing_id", "wy_filing_date",
  "llc_ein", "wy_registered_agent_name", "wy_registered_agent_address",
  "signing_officer_name", "signing_officer_title", "peo_name",
  "effective_date", "execution_date", "execution_location",
  "llc_principal_address",
]);


export default function FormACompany() {
  const { user } = useAuth();
  const { unaId } = useMemberUna();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [formation, setFormation] = useState<Formation | null>(null);
  const [docs, setDocs] = useState<FormationDoc[]>([]);

  // Load or create the member's active formation.
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      setLoading(true);
      const { data: existing } = await supabase
        .from("llc_formations")
        .select("*")
        .eq("owner_user_id", user.id)
        .eq("not_proceeded", false)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (existing) {
        setFormation(existing as Formation);
      } else {
        const { data: created, error } = await supabase
          .from("llc_formations")
          .insert({ owner_user_id: user.id, una_id: unaId })
          .select("*")
          .single();
        if (error) {
          toast({ title: "Could not start a formation", description: error.message, variant: "destructive" });
        } else {
          setFormation(created as Formation);
        }
      }
      setLoading(false);
    })();
  }, [user?.id, unaId, toast]);

  // Load docs
  useEffect(() => {
    if (!formation?.id) return;
    (async () => {
      const { data } = await supabase
        .from("llc_formation_documents")
        .select("id, document_kind, storage_path, wave, generation_version, generated_at, executed_scan_storage_path, executed_scan_uploaded_at")
        .eq("formation_id", formation.id)
        .order("generated_at", { ascending: false });
      setDocs((data as FormationDoc[]) || []);
    })();
  }, [formation?.id, formation?.wave_one_generated_at]);

  async function persist(patch: Partial<Formation>) {
    if (!formation) return;
    setSaving(true);
    const { data, error } = await supabase
      .from("llc_formations")
      .update(patch)
      .eq("id", formation.id)
      .select("*")
      .single();
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return null;
    }
    setFormation(data as Formation);
    return data as Formation;
  }

  async function invokeGenerate(kind: "wave1" | "articles_pack" | "wave2_governance" | "wave2_banking" | "wave3_officers") {
    if (!formation) return null;
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("llc-formation-generate", {
        body: { formation_id: formation.id, kind },
      });
      if (error) {
        let msg = error.message;
        try { const j = await (error as any).context?.json?.(); if (j?.error) msg = j.error; } catch {}
        toast({ title: "Generation failed", description: msg, variant: "destructive" });
        return null;
      }
      // Refresh
      const { data: fresh } = await supabase.from("llc_formations").select("*").eq("id", formation.id).single();
      if (fresh) setFormation(fresh as Formation);
      const { data: freshDocs } = await supabase
        .from("llc_formation_documents").select("id, document_kind, storage_path, wave, generation_version, generated_at, executed_scan_storage_path, executed_scan_uploaded_at")
        .eq("formation_id", formation.id).order("generated_at", { ascending: false });
      setDocs((freshDocs as FormationDoc[]) || []);
      return data;
    } finally { setGenerating(false); }
  }

  async function openDoc(storage_path: string, name: string) {
    try {
      const url = await getDocumentSignedUrl({ file_path: storage_path, document_name: name } as any, { download: false });
      window.open(url, "_blank", "noopener");
    } catch (e: any) {
      toast({ title: "Could not open document", description: e.message, variant: "destructive" });
    }
  }

  if (loading || !formation) {
    return (
      <MainLayout>
        <div className="container max-w-3xl mx-auto py-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (formation.not_proceeded) {
    return (
      <MainLayout>
        <div className="container max-w-3xl mx-auto py-10">
          <Card>
            <CardHeader>
              <CardTitle>You paused this formation</CardTitle>
              <CardDescription>Nothing was filed. When you are ready to talk it through, reach a person on the Support page.</CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2">
              <Button onClick={() => navigate("/support")}>Ask a person</Button>
              <Button variant="ghost" onClick={() => navigate("/governance")}>Back to Governance</Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const stage = Math.min(8, formation.stage || 1);
  const stageIndex = STAGES.findIndex(s => s.n === stage);
  const pct = stageIndex < 0 ? 0 : (stageIndex / (STAGES.length - 1)) * 100;

  return (
    <MainLayout>
      <div className="container max-w-3xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        <div className="mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight title-rule">Form a Company</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Prepare the documents you will use to form a Wyoming LLC owned by your Association. You file with Wyoming and apply for the EIN yourself. No tax advice, no filings on your behalf.
          </p>
        </div>

        <div className="mb-6">
          <Progress value={pct} className="h-2" />
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
            {STAGES.map(s => (
              <span key={s.n} className={s.n === stage ? "font-semibold text-foreground" : ""}>
                {s.n}. {s.label}{s.n < 8 ? " · " : ""}
              </span>
            ))}
          </div>
        </div>

        {stage === 1 && <Stage1 formation={formation} persist={persist} setStage={(n) => persist({ stage: n })} onGenerate={() => invokeGenerate("wave1")} generating={generating} saving={saving} docs={docs} openDoc={openDoc} />}
        {stage === 2 && <Stage2 formation={formation} persist={persist} setStage={(n) => persist({ stage: n })} onGeneratePack={() => invokeGenerate("articles_pack")} generating={generating} docs={docs} openDoc={openDoc} />}
        {stage === 3 && <Stage3 formation={formation} persist={persist} setStage={(n) => persist({ stage: n })} />}
        {stage === 4 && <Stage4 formation={formation} persist={persist} />}
        {stage === 5 && <Stage5 formation={formation} persist={persist} setStage={(n) => persist({ stage: n })} onGenerate={() => invokeGenerate("wave2_governance")} generating={generating} docs={docs} openDoc={openDoc} />}
        {stage === 6 && <Stage6 formation={formation} persist={persist} setStage={(n) => persist({ stage: n })} onGenerate={() => invokeGenerate("wave2_banking")} generating={generating} docs={docs} openDoc={openDoc} />}
        {stage === 7 && <Stage7 formation={formation} persist={persist} setStage={(n) => persist({ stage: n })} onGenerate={() => invokeGenerate("wave3_officers")} generating={generating} docs={docs} openDoc={openDoc} />}
        {stage === 8 && <Stage8 formation={formation} docs={docs} openDoc={openDoc} refresh={async () => {
          const { data: freshDocs } = await supabase
            .from("llc_formation_documents").select("id, document_kind, storage_path, wave, generation_version, generated_at, executed_scan_storage_path, executed_scan_uploaded_at")
            .eq("formation_id", formation.id).order("generated_at", { ascending: false });
          setDocs((freshDocs as FormationDoc[]) || []);
        }} />}
      </div>
    </MainLayout>
  );
}

// ==================== STAGE 1 ====================
function Stage1(props: {
  formation: Formation;
  persist: (p: Partial<Formation>) => Promise<Formation | null>;
  setStage: (n: number) => void;
  onGenerate: () => Promise<any>;
  generating: boolean;
  saving: boolean;
  docs: FormationDoc[];
  openDoc: (path: string, name: string) => void;
}) {
  const { formation, persist, setStage, onGenerate, generating, docs, openDoc } = props;
  const [entityName, setEntityName] = useState(formation.entity_name || "");
  const [purpose, setPurpose] = useState(formation.purpose_text || "");
  const [closeLlc, setCloseLlc] = useState(formation.close_llc);
  const [attested, setAttested] = useState(formation.timing_attested);
  const [showStop, setShowStop] = useState(false);

  const bannedHit = useMemo(() => findBannedPurposeTerm(purpose), [purpose]);

  const nameOk = /(llc|l\.l\.c\.|limited liability company)/i.test(entityName.trim());
  const canComplete = !!entityName && nameOk && !!purpose.trim() && !bannedHit && attested;

  async function handleComplete() {
    // Persist final answers (only final purpose text stored, no drafts).
    await persist({
      entity_name: entityName.trim(),
      purpose_text: purpose.trim(),
      close_llc: closeLlc,
      timing_attested: true,
      timing_attested_at: new Date().toISOString(),
      ownership_fork: "una_owned",
    });
    const result = await onGenerate();
    if (result) setStage(2);
  }

  async function handleCannotAttest() {
    await persist({ not_proceeded: true });
  }

  const alreadyGenerated = !!formation.wave_one_generated_at;

  return (
    <div className="space-y-6">
      {/* Teaching */}
      <Card>
        <CardHeader>
          <CardTitle>What this wizard does, and does not do</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p><strong>Prepares your documents</strong> so you can form a Wyoming LLC owned by your Association.</p>
          <p><strong>You file with Wyoming.</strong> You also apply for the EIN yourself. This wizard walks you through both.</p>
          <p className="text-muted-foreground">This is not tax advice and this is not a promise of any legal outcome.</p>
        </CardContent>
      </Card>

      {/* Ownership */}
      <Card>
        <CardHeader>
          <CardTitle>Ownership</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup value="una_owned">
            <label className="flex items-start gap-3 rounded-md border p-3">
              <RadioGroupItem value="una_owned" id="una_owned" className="mt-1" />
              <div>
                <div className="font-medium">Owned by your Association</div>
                <div className="text-xs text-muted-foreground">The Association is the sole owner. This is the standard path.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 rounded-md border p-3 opacity-60 cursor-not-allowed">
              <RadioGroupItem value="second_tier" id="second_tier" disabled className="mt-1" />
              <div>
                <div className="font-medium">Owned by one of your companies</div>
                <div className="text-xs text-muted-foreground">Available after counsel review.</div>
              </div>
            </label>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Close election */}
      <Card>
        <CardHeader>
          <CardTitle>Close company election</CardTitle>
        </CardHeader>
        <CardContent>
          <label className="flex items-start gap-3">
            <Checkbox checked={closeLlc} onCheckedChange={(v) => setCloseLlc(!!v)} />
            <span className="text-sm">
              Elect the Wyoming Close LLC form. The Close election keeps ownership and management tightly held with fewer public reporting burdens.
            </span>
          </label>
        </CardContent>
      </Card>

      {/* Entity name */}
      <Card>
        <CardHeader>
          <CardTitle>Entity name</CardTitle>
          <CardDescription>Wyoming requires a limited liability company designator such as "LLC" or "Limited Liability Company".</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="entity_name">Proposed name</Label>
          <Input id="entity_name" value={entityName} onChange={e => setEntityName(e.target.value)} placeholder="e.g., Cornerstone Provisions LLC" />
          {entityName && !nameOk && (
            <p className="text-xs text-amber-700">Add "LLC", "L.L.C.", or "Limited Liability Company" to the name.</p>
          )}
        </CardContent>
      </Card>

      {/* Purpose */}
      <Card>
        <CardHeader>
          <CardTitle>Purpose</CardTitle>
          <CardDescription>Describe the commerce this company will conduct in service of the mission.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="rounded-md bg-muted/40 p-3 text-xs">
            <div className="font-semibold mb-1">Sample language</div>
            <p>"To provide provisions and operational support to the Association's ministry, including sourcing, stewarding, and distributing goods used in worship and community service."</p>
          </div>
          <Textarea rows={5} value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="Describe the commerce this company will conduct." />
          {bannedHit && (
            <Alert variant="destructive">
              <ShieldAlert className="w-4 h-4" />
              <AlertDescription>
                The phrase <strong>"{bannedHit}"</strong> is not appropriate here. Restate the purpose in terms of the commerce the company will conduct in service of the mission. Please remove language about taxes, creditors, or hiding assets.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Timing attestation */}
      <Card>
        <CardHeader>
          <CardTitle>Timing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-start gap-3">
            <Checkbox checked={attested} onCheckedChange={(v) => setAttested(!!v)} />
            <span className="text-sm">
              This formation is not undertaken to hinder, delay, or defraud any creditor, and no transfer subject to any active or anticipated dispute is contemplated.
            </span>
          </label>
          <button type="button" className="text-xs underline text-muted-foreground" onClick={() => setShowStop(true)}>
            I cannot check this box
          </button>
          {showStop && (
            <Alert>
              <AlertDescription className="text-sm space-y-3">
                <p>That is the right instinct. Do not proceed with formation right now. Reach a person and talk it through first.</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCannotAttest}>Pause and ask a person</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowStop(false)}>Go back</Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {alreadyGenerated && docs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Documents ready</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {docs.filter(d => d.wave === 1).map(d => (
              <div key={d.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
                <span>{prettyKind(d.document_kind)}</span>
                <Button size="sm" variant="ghost" onClick={() => openDoc(d.storage_path, prettyKind(d.document_kind))}>Open</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={() => history.back()}>Cancel</Button>
        <Button disabled={!canComplete || generating} onClick={handleComplete}>
          {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing your documents</> : <>Complete Stage 1 <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}

// ==================== STAGE 2 ====================
function Stage2(props: {
  formation: Formation;
  persist: (p: Partial<Formation>) => Promise<Formation | null>;
  setStage: (n: number) => void;
  onGeneratePack: () => Promise<any>;
  generating: boolean;
  docs: FormationDoc[];
  openDoc: (path: string, name: string) => void;
}) {
  const { formation, persist, setStage, onGeneratePack, generating, docs, openDoc } = props;
  const ff = formation.form_fields || {};
  const [ra, setRa] = useState(ff.registered_agent_name || "");
  const [raAddr, setRaAddr] = useState(ff.registered_agent_address || "");
  const [principal, setPrincipal] = useState(ff.principal_office_address || "");
  const [organizer, setOrganizer] = useState(ff.organizer_name || "");
  const [filed, setFiled] = useState(false);

  const canGenerate = ra && raAddr && principal && organizer;
  const pack = docs.find(d => d.document_kind === "articles_data_pack");

  async function saveFields() {
    await persist({
      form_fields: {
        ...ff,
        registered_agent_name: ra,
        registered_agent_address: raAddr,
        principal_office_address: principal,
        organizer_name: organizer,
      },
    });
  }

  async function handleGenerate() {
    await saveFields();
    await onGeneratePack();
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Wyoming filing preparation</CardTitle>
          <CardDescription>Enter the fields you will submit to the Wyoming Secretary of State portal. For a clean footprint, see the guidance in your Library.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Registered agent name</Label><Input value={ra} onChange={e => setRa(e.target.value)} /></div>
          <div><Label>Registered agent address (Wyoming)</Label><Input value={raAddr} onChange={e => setRaAddr(e.target.value)} placeholder="Street, city, Wyoming, ZIP" /></div>
          <div><Label>Principal office address</Label><Input value={principal} onChange={e => setPrincipal(e.target.value)} /></div>
          <div><Label>Organizer name</Label><Input value={organizer} onChange={e => setOrganizer(e.target.value)} /></div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={saveFields}>Save</Button>
            <Button disabled={!canGenerate || generating} onClick={handleGenerate}>
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing</> : "Generate Articles Data Pack"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {pack && (
        <Card>
          <CardHeader>
            <CardTitle>Your Articles Data Pack</CardTitle>
            <CardDescription>Use this one page as your reference when filing on the Wyoming portal. The current fee is shown on the portal.</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-between items-center">
            <span className="text-sm">Articles Data Pack (PDF)</span>
            <Button size="sm" variant="outline" onClick={() => openDoc(pack.storage_path, "Articles Data Pack")}>Open</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Confirm your filing</CardTitle>
          <CardDescription>Complete Stage 2 once you have filed the Articles on the Wyoming portal.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={filed} onCheckedChange={(v) => setFiled(!!v)} />
            <span>I have filed the Articles of Organization with the Wyoming Secretary of State and I have my filing confirmation.</span>
          </label>
          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStage(1)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <Button disabled={!filed} onClick={() => setStage(3)}>Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== STAGE 3 ====================
function Stage3(props: {
  formation: Formation;
  persist: (p: Partial<Formation>) => Promise<Formation | null>;
  setStage: (n: number) => void;
}) {
  const { formation, persist, setStage } = props;
  const { toast } = useToast();
  const [filingId, setFilingId] = useState(formation.wy_filing_id || "");
  const [filingDate, setFilingDate] = useState(formation.wy_filing_date || "");
  const [reconciledName, setReconciledName] = useState("");
  const [nameChanged, setNameChanged] = useState<null | { from: string; to: string }>(null);

  const idOk = /^\d{2,}(-?\d+)?$/.test(filingId.trim());
  const dateOk = !!filingDate;
  const nameOk = reconciledName.trim().length > 3;

  async function handleContinue() {
    const trimmedFiled = reconciledName.trim();
    const patch: Partial<Formation> = {
      wy_filing_id: filingId.trim(),
      wy_filing_date: filingDate,
    };
    if (trimmedFiled && trimmedFiled !== (formation.entity_name || "").trim()) {
      patch.entity_name = trimmedFiled;
      setNameChanged({ from: formation.entity_name || "", to: trimmedFiled });
    }
    patch.stage = 4;
    const res = await persist(patch);
    if (res) {
      toast({ title: "Filing recorded" });
      setStage(4);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Filing confirmation</CardTitle>
          <CardDescription>Enter the Wyoming Filing ID and date exactly as shown on your confirmation.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Wyoming Filing ID</Label><Input value={filingId} onChange={e => setFilingId(e.target.value)} placeholder="e.g., 2026-001234567" /></div>
          <div><Label>Filing date</Label><Input type="date" value={filingDate} onChange={e => setFilingDate(e.target.value)} /></div>
          <div>
            <Label>Entity name, exactly as it appears on the filed Articles</Label>
            <Input value={reconciledName} onChange={e => setReconciledName(e.target.value)} placeholder={formation.entity_name || ""} />
            <p className="text-xs text-muted-foreground mt-1">If Wyoming adjusted the name at filing, enter the filed name here. The filed name is the one your documents will use going forward.</p>
          </div>
          {nameChanged && (
            <Alert>
              <AlertDescription className="text-sm">
                The filed name differs from your Stage 1 name. We updated your formation from <strong>{nameChanged.from}</strong> to <strong>{nameChanged.to}</strong>. Any documents generated in the next release will use the filed name.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-between pt-2">
            <Button variant="ghost" onClick={() => setStage(2)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <Button disabled={!idOk || !dateOk || !nameOk} onClick={handleContinue}>Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== STAGE 4 ====================
function Stage4(props: {
  formation: Formation;
  persist: (p: Partial<Formation>) => Promise<Formation | null>;
}) {
  const { formation, persist } = props;
  const { toast } = useToast();
  const { user } = useAuth();
  const [ein, setEin] = useState(formation.llc_ein || "");
  const [rpRole, setRpRole] = useState<Formation["rp_role"]>(formation.rp_role);
  const [thirdParty, setThirdParty] = useState(formation.has_third_party_designee);
  const [uploadedPath, setUploadedPath] = useState(formation.cp575_storage_path || "");
  const [uploading, setUploading] = useState(false);

  const einOk = /^\d{2}-\d{7}$/.test(ein.trim());

  async function handleUpload(file: File) {
    if (!user?.id) return;
    setUploading(true);
    const path = `${user.id}/llc-formations/${formation.id}/cp575-${Date.now()}-${file.name}`;
    let error: { message: string } | null = null;
    if (file.size > MAX_UPLOAD_BYTES) {
      error = { message: FILE_TOO_LARGE_MESSAGE };
    } else {
      try {
        await uploadFileToStorage({ bucket: "documents", path, file, upsert: true });
      } catch (e: any) {
        error = { message: e?.message || "Your file did not save. Try again." };
      }
    }
    if (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } else {
      await supabase.rpc("set_storage_object_owner", { p_bucket: "documents", p_path: path, p_owner: user.id });
      setUploadedPath(path);
      await persist({ cp575_storage_path: path });
      toast({ title: "CP 575 saved to your Record" });
    }
    setUploading(false);
  }

  async function handleSave() {
    if (!einOk) return;
    await persist({
      llc_ein: ein.trim(),
      rp_role: rpRole,
      has_third_party_designee: thirdParty,
      stage: 5,
    });
  }

  const stageComplete = einOk && !!uploadedPath;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>EIN</CardTitle>
          <CardDescription>You apply for the LLC's EIN yourself.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>Apply online at the IRS website using Form SS-4. The application asks who the Responsible Party is. Choose the path below that matches your plan.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Responsible Party role</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup value={rpRole} onValueChange={(v) => setRpRole(v as any)}>
            <label className="flex items-start gap-3 rounded-md border p-3">
              <RadioGroupItem value="manager_default" id="mgr" className="mt-1" />
              <div>
                <div className="font-medium">Manager serves as Responsible Party</div>
                <div className="text-xs text-muted-foreground">Default path. Nothing extra to sign.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 rounded-md border p-3">
              <RadioGroupItem value="designated_third_party" id="third" className="mt-1" />
              <div>
                <div className="font-medium">Designated third party</div>
                <div className="text-xs text-muted-foreground">A named person serving by status as an Officer or agent of the Association. The appointing resolution and certification will generate with your governance documents in the next release.</div>
              </div>
            </label>
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Third Party Designee</CardTitle>
          <CardDescription>Is someone else completing the SS-4 as a Third Party Designee on your behalf?</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={thirdParty ? "yes" : "no"} onValueChange={(v) => setThirdParty(v === "yes")}>
            <label className="flex items-center gap-2"><RadioGroupItem value="no" id="tpd_no" /> No</label>
            <label className="flex items-center gap-2"><RadioGroupItem value="yes" id="tpd_yes" /> Yes</label>
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>EIN and CP 575</CardTitle>
          <CardDescription>Enter the EIN and upload your CP 575 confirmation letter.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>EIN</Label>
            <Input value={ein} onChange={e => setEin(e.target.value)} placeholder="12-3456789" />
            {ein && !einOk && <p className="text-xs text-amber-700 mt-1">Format is two digits, a dash, then seven digits.</p>}
          </div>
          <div>
            <Label>CP 575 upload</Label>
            <div className="flex items-center gap-2">
              <Input type="file" accept="application/pdf,image/*" onChange={e => e.target.files && handleUpload(e.target.files[0])} disabled={uploading} />
              {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
              {uploadedPath && <Badge variant="secondary">Saved</Badge>}
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button disabled={!stageComplete} onClick={handleSave}>Save stage</Button>
          </div>
        </CardContent>
      </Card>

      {stageComplete && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Ready for your governance package</CardTitle>
            <CardDescription>You have everything the wizard needs. Continue to Stage 5 to prepare the Operating Agreement and related documents.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleSave}>Continue to Stage 5 <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== helpers ====================
function prettyKind(k: string | null): string {
  switch (k) {
    case "authorization_resolution": return "UNA Resolution: Authorization to Form Subsidiary LLC";
    case "execution_checklist": return "Execution Checklist";
    case "articles_data_pack": return "Articles Data Pack";
    case "full_operating_agreement": return "LLC Full Operating Agreement";
    case "short_operating_agreement": return "LLC Short Form Operating Agreement";
    case "appointment_of_manager": return "UNA Resolution: Appointment of Manager";
    case "manager_certification_rp": return "Manager's Certification of Responsible Party";
    case "appointment_of_rp": return "UNA Resolution: Appointment of Responsible Party";
    case "second_authorizer_designation": return "Designation of Second Authorizing Individual";
    case "member_standing_consent": return "Member Standing Consent for Extraordinary Transactions";
    case "banking_resolution": return "LLC Banking Resolution";
    case "beneficial_ownership": return "LLC Beneficial Ownership Declaration";
    case "certificate_of_org_authority": return "Certificate of Organization, Ownership and Authority";
    case "certificate_of_incumbency": return "LLC Certificate of Incumbency";
    case "source_of_funds_declaration": return "Declaration of Source of Funds and Wealth";
    case "officer_preconditions_amendment": return "UNA Amendment Resolution: Emergency Stewardship Articles";
    case "officer_preconditions_master_sub": return "Master Subsidiary Governance Resolution";
    case "officer_office_creation": return "UNA Resolution: Creation of an Office";
    case "officer_appointment_bundle": return "Officer Appointment, Acceptance, and Conditional Resignation";
    case "officer_successor_acceptance": return "Pre-Authorized Successor Manager Acceptance";
    case "officer_execution_checklist": return "Execution Checklist: Officer";
    default: return k || "Document";
  }
}

// ==================== STAGE 5 ====================
type MergeField = { type: string; label: string; required: boolean };

function Stage5(props: {
  formation: Formation;
  persist: (p: Partial<Formation>) => Promise<Formation | null>;
  setStage: (n: number) => void;
  onGenerate: () => Promise<any>;
  generating: boolean;
  docs: FormationDoc[];
  openDoc: (path: string, name: string) => void;
}) {
  const { formation, persist, setStage, onGenerate, generating, docs, openDoc } = props;
  const { toast } = useToast();
  const ff = formation.form_fields || {};

  const [loadingFields, setLoadingFields] = useState(true);
  const [fieldMap, setFieldMap] = useState<Record<string, MergeField>>({});
  const [values, setValues] = useState<Record<string, string>>({ ...ff });
  const [samePersonNotary, setSamePersonNotary] = useState(!!ff.same_person_notary);
  const [addCp2, setAddCp2] = useState(!!ff.cp2_name);

  // Load union of merge fields from wave 2 templates, minus captured/handled ones.
  useEffect(() => {
    (async () => {
      setLoadingFields(true);
      let ids = WAVE2_TEMPLATE_IDS;
      // Skip Appointment of RP fields when not on the third-party path.
      if (formation.rp_role !== "designated_third_party") {
        ids = ids.filter(id => id !== "fe754908-a990-47ff-8f53-da1ee6cea73f");
      }
      const { data } = await supabase
        .from("document_templates")
        .select("id, merge_fields")
        .in("id", ids);
      const merged: Record<string, MergeField> = {};
      (data || []).forEach((row: any) => {
        const mf = row.merge_fields || {};
        Object.entries(mf).forEach(([k, v]: [string, any]) => {
          if (CAPTURED_FIELDS.has(k)) return;
          // cp2_* handled by explicit toggle
          if (k.startsWith("cp2_")) return;
          if (!merged[k]) merged[k] = v as MergeField;
        });
      });
      setFieldMap(merged);
      setLoadingFields(false);
    })();
  }, [formation.rp_role]);

  const bannedFundsHit = useMemo(
    () => findBannedPurposeTerm(values.funds_source_description || ""),
    [values.funds_source_description],
  );

  const requiredKeys = Object.entries(fieldMap).filter(([, f]) => f.required).map(([k]) => k);
  const missingRequired = requiredKeys.filter(k => !(values[k] && String(values[k]).trim()));
  const canGenerate = missingRequired.length === 0 && !bannedFundsHit;

  const alreadyGenerated = docs.some(d => d.wave === 2 && d.document_kind === "full_operating_agreement");

  async function handleGenerate() {
    const patch: Record<string, any> = {
      ...ff,
      ...values,
      same_person_notary: samePersonNotary,
    };
    if (!addCp2) {
      // Explicitly clear cp2 fields on the solo path.
      delete patch.cp2_name;
      delete patch.cp2_dob;
      delete patch.cp2_id_type;
    }
    const updated = await persist({ form_fields: patch });
    if (!updated) return;
    const result = await onGenerate();
    if (result) toast({ title: "Governance documents ready" });
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Remaining details</CardTitle>
          <CardDescription>Fill in what your governance and banking documents still need. Fields you already entered earlier are not repeated here.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loadingFields ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading fields</div>
          ) : Object.keys(fieldMap).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing more to capture. You are ready to generate.</p>
          ) : (
            Object.entries(fieldMap).map(([key, field]) => (
              <div key={key}>
                <Label>{field.label}{field.required ? "" : " (optional)"}</Label>
                {key === "funds_source_description" ? (
                  <>
                    <div className="rounded-md bg-muted/40 p-3 text-xs mb-1">
                      <div className="font-semibold mb-1">Sample language</div>
                      <p>"Contributions from its members derived from employment income and personal savings, and revenue from the Association's lawful stewardship activities."</p>
                    </div>
                    <Textarea
                      rows={3}
                      value={values[key] || ""}
                      onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
                    />
                    {bannedFundsHit && (
                      <Alert variant="destructive" className="mt-2">
                        <ShieldAlert className="w-4 h-4" />
                        <AlertDescription>
                          The phrase <strong>"{bannedFundsHit}"</strong> is not appropriate here. Please restate in plain terms about where the money comes from.
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                ) : (
                  <Input
                    type={field.type === "date" ? "date" : "text"}
                    value={values[key] || ""}
                    onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
                  />
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Same person notary</CardTitle>
          <CardDescription>Check this if the certifying Manager and any second control person are the same individual. Your notary blocks will assemble accordingly.</CardDescription>
        </CardHeader>
        <CardContent>
          <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={samePersonNotary} onCheckedChange={(v) => setSamePersonNotary(!!v)} />
            <span>Yes, they are the same individual.</span>
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Second control person (optional)</CardTitle>
          <CardDescription>If a second individual will also hold authority in the Company, add them here. If not, leave this off and the wizard will assemble the single-person path.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={addCp2} onCheckedChange={(v) => setAddCp2(!!v)} />
            <span>Add a second control person.</span>
          </label>
          {addCp2 && (
            <div className="space-y-2">
              <div><Label>Second person full legal name</Label><Input value={values.cp2_name || ""} onChange={e => setValues(v => ({ ...v, cp2_name: e.target.value }))} /></div>
              <div><Label>Second person date of birth</Label><Input type="date" value={values.cp2_dob || ""} onChange={e => setValues(v => ({ ...v, cp2_dob: e.target.value }))} /></div>
              <div><Label>Second person ID type</Label><Input value={values.cp2_id_type || ""} onChange={e => setValues(v => ({ ...v, cp2_id_type: e.target.value }))} placeholder="e.g., Driver License" /></div>
            </div>
          )}
        </CardContent>
      </Card>

      {alreadyGenerated && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Your governance documents</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {docs.filter(d => d.wave === 2 && d.document_kind !== "banking_resolution" && d.document_kind !== "beneficial_ownership" && d.document_kind !== "certificate_of_org_authority" && d.document_kind !== "certificate_of_incumbency" && d.document_kind !== "source_of_funds_declaration").map(d => (
              <div key={d.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
                <span>{prettyKind(d.document_kind)}</span>
                <Button size="sm" variant="ghost" onClick={() => openDoc(d.storage_path, prettyKind(d.document_kind))}>Open</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={() => setStage(4)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <div className="flex gap-2">
          <Button disabled={!canGenerate || generating} onClick={handleGenerate}>
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing</> : (alreadyGenerated ? "Regenerate governance package" : "Generate governance package")}
          </Button>
          {alreadyGenerated && (
            <Button onClick={() => setStage(6)}>Continue to banking <ArrowRight className="w-4 h-4 ml-2" /></Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== STAGE 6 ====================
function Stage6(props: {
  formation: Formation;
  persist: (p: Partial<Formation>) => Promise<Formation | null>;
  setStage: (n: number) => void;
  onGenerate: () => Promise<any>;
  generating: boolean;
  docs: FormationDoc[];
  openDoc: (path: string, name: string) => void;
}) {
  const { formation, setStage, onGenerate, generating, docs, openDoc, persist } = props;
  const bankKinds = new Set(["banking_resolution", "beneficial_ownership", "certificate_of_org_authority", "certificate_of_incumbency", "source_of_funds_declaration"]);
  const bankDocs = docs.filter(d => d.wave === 2 && bankKinds.has(d.document_kind || ""));
  const alreadyGenerated = bankDocs.length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Banking package</CardTitle>
          <CardDescription>These are the documents banks and compliance reviewers ask for when opening or reviewing the Company's account.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p>Includes the Banking Resolution, Beneficial Ownership Declaration, Certificate of Organization, Ownership and Authority, Certificate of Incumbency, and Declaration of Source of Funds and Wealth. A regenerated Execution Checklist accompanies them.</p>
        </CardContent>
      </Card>

      {alreadyGenerated && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Your banking documents</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {bankDocs.map(d => (
              <div key={d.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
                <span>{prettyKind(d.document_kind)}</span>
                <Button size="sm" variant="ghost" onClick={() => openDoc(d.storage_path, prettyKind(d.document_kind))}>Open</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={() => setStage(5)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <div className="flex gap-2">
          <Button disabled={generating} onClick={onGenerate}>
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing</> : (alreadyGenerated ? "Regenerate banking package" : "Generate banking package")}
          </Button>
          {alreadyGenerated && (
            <Button onClick={() => persist({ stage: 7 })}>Continue to Officer <ArrowRight className="w-4 h-4 ml-2" /></Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== STAGE 8 ====================
function Stage8(props: {
  formation: Formation;
  docs: FormationDoc[];
  openDoc: (path: string, name: string) => void;
  refresh: () => Promise<void>;
}) {
  const { formation, docs, openDoc, refresh } = props;
  const { user } = useAuth();
  const { toast } = useToast();
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const generated = docs.filter(d => d.document_kind !== "articles_data_pack");

  async function handleUpload(doc: FormationDoc, file: File) {
    if (!user?.id) return;
    setUploadingId(doc.id);
    const path = `${user.id}/llc-formations/${formation.id}/executed/${doc.id}-${file.name}`;
    let error: { message: string } | null = null;
    if (file.size > MAX_UPLOAD_BYTES) {
      error = { message: FILE_TOO_LARGE_MESSAGE };
    } else {
      try {
        await uploadFileToStorage({ bucket: "documents", path, file, upsert: true });
      } catch (e: any) {
        error = { message: e?.message || "Your file did not save. Try again." };
      }
    }
    if (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } else {
      await supabase.rpc("set_storage_object_owner", { p_bucket: "documents", p_path: path, p_owner: user.id });
      await supabase
        .from("llc_formation_documents")
        .update({ executed_scan_storage_path: path, executed_scan_uploaded_at: new Date().toISOString() })
        .eq("id", doc.id);
      toast({ title: "Signed scan saved" });
      await refresh();
    }
    setUploadingId(null);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Record and verify</CardTitle>
          <CardDescription>Upload the signed scan of each document so your Record shows the executed version alongside the generated original.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {generated.map(d => (
            <div key={d.id} className="rounded-md border p-3 text-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium">{prettyKind(d.document_kind)}</span>
                <div className="flex items-center gap-2">
                  {d.executed_scan_storage_path && <Badge variant="secondary">Signed scan on file</Badge>}
                  <Button size="sm" variant="ghost" onClick={() => openDoc(d.storage_path, prettyKind(d.document_kind))}>Open original</Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input type="file" accept="application/pdf,image/*" disabled={uploadingId === d.id} onChange={e => e.target.files && handleUpload(d, e.target.files[0])} />
                {uploadingId === d.id && <Loader2 className="w-4 h-4 animate-spin" />}
                {d.executed_scan_storage_path && (
                  <Button size="sm" variant="ghost" onClick={() => openDoc(d.executed_scan_storage_path!, `${prettyKind(d.document_kind)} (signed)`)}>Open signed</Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {Array.isArray(formation.form_fields?.stage7_access_map) && formation.form_fields.stage7_access_map.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Access map confirmation</CardTitle>
            <CardDescription>Confirm the access below matches what was actually granted to the officer. Names and references only. Never a password, key, or seed phrase.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            <ul className="list-disc pl-5 space-y-1">
              {(formation.form_fields.stage7_access_map as Array<{ label?: string; notes?: string }>)
                .filter(a => a && (a.label || a.notes))
                .map((a, i) => (
                  <li key={i}>{a.label || ""}{a.notes ? `: ${a.notes}` : ""}</li>
                ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>What done looks like</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p>Your Association has adopted its authorizing resolution. Wyoming holds your Articles. The IRS has issued your EIN. Your Operating Agreement, Manager appointment, banking package, and dual-authorization instrument are executed and stored here in your Record.</p>
          <p>A physical binder is optional. A fully digital Record is a valid governance record. Print copies only when a specific counterparty asks for one; your Record here is the official one.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== STAGE 7 (Officer flow) ====================
function Stage7(props: {
  formation: Formation;
  persist: (p: Partial<Formation>) => Promise<Formation | null>;
  setStage: (n: number) => void;
  onGenerate: () => Promise<any>;
  generating: boolean;
  docs: FormationDoc[];
  openDoc: (path: string, name: string) => void;
}) {
  const { formation, persist, setStage, onGenerate, generating, docs, openDoc } = props;
  const { toast } = useToast();
  const ff = formation.form_fields || {};
  const required = stage7Required(formation);

  const [articlesAdopted, setArticlesAdopted] = useState<null | boolean>(
    typeof ff.stage7_articles_adopted === "boolean" ? ff.stage7_articles_adopted : null,
  );
  const [masterSubAdopted, setMasterSubAdopted] = useState<null | boolean>(
    typeof ff.stage7_master_sub_adopted === "boolean" ? ff.stage7_master_sub_adopted : null,
  );

  const [officerName, setOfficerName] = useState<string>(ff.stage7_officer_name || "");
  const [officeTitle, setOfficeTitle] = useState<string>(ff.stage7_office_title || "");
  const [isNewOffice, setIsNewOffice] = useState<null | boolean>(
    typeof ff.stage7_is_new_office === "boolean" ? ff.stage7_is_new_office : null,
  );
  const [officeScope, setOfficeScope] = useState<string>(ff.stage7_office_scope_items || "");
  const [officeLimits, setOfficeLimits] = useState<string>(ff.stage7_office_limit_items || "");

  const [successorName, setSuccessorName] = useState<string>(ff.stage7_successor_name || "");

  type AccessRow = { label: string; notes: string };
  const initialAccess: AccessRow[] = Array.isArray(ff.stage7_access_map) && ff.stage7_access_map.length > 0
    ? (ff.stage7_access_map as any[]).map((a) => ({ label: a.label || "", notes: a.notes || "" }))
    : [{ label: "", notes: "" }];
  const [accessMap, setAccessMap] = useState<AccessRow[]>(initialAccess);

  const wave3Docs = docs.filter(d => d.wave === 3);
  const alreadyGenerated = wave3Docs.length > 0;

  const preconditionsAnswered = articlesAdopted !== null && masterSubAdopted !== null;
  const officeDetailsReady = officerName.trim().length > 1
    && officeTitle.trim().length > 1
    && isNewOffice !== null
    && (isNewOffice === false || officeScope.trim().length > 0);
  const canGenerate = preconditionsAnswered && officeDetailsReady;

  function updateAccess(i: number, patch: Partial<AccessRow>) {
    setAccessMap(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  }
  function addAccessRow() {
    setAccessMap(prev => [...prev, { label: "", notes: "" }]);
  }
  function removeAccessRow(i: number) {
    setAccessMap(prev => prev.filter((_, idx) => idx !== i));
  }

  async function persistStage7Fields(extras: Record<string, any> = {}) {
    return persist({
      form_fields: {
        ...ff,
        stage7_articles_adopted: articlesAdopted,
        stage7_master_sub_adopted: masterSubAdopted,
        stage7_officer_name: officerName.trim(),
        stage7_office_title: officeTitle.trim(),
        stage7_is_new_office: isNewOffice,
        stage7_office_scope_items: officeScope.trim(),
        stage7_office_limit_items: officeLimits.trim(),
        stage7_successor_name: successorName.trim(),
        stage7_access_map: accessMap.filter(a => a.label || a.notes),
        stage7_skipped: false,
        ...extras,
      },
    });
  }

  async function handleGenerate() {
    const saved = await persistStage7Fields();
    if (!saved) return;
    const result = await onGenerate();
    if (result) toast({ title: "Officer bundle ready" });
  }

  async function handleSkip() {
    await persist({
      form_fields: {
        ...ff,
        stage7_skipped: true,
      },
      stage: 8,
    });
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Officer appointment</CardTitle>
          <CardDescription>
            {required
              ? "Because a second individual holds authority in this Company, an officer instrument must be in place before you finish."
              : "You can add officers any time later; see the Officer Trust Kit guide in the Library."}
          </CardDescription>
        </CardHeader>
      </Card>

      {!required && !alreadyGenerated && (
        <div className="flex justify-end">
          <Button variant="ghost" onClick={handleSkip}>Skip for now, continue to Record</Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Preconditions</CardTitle>
          <CardDescription>The Board adopts these before the officer instruments are executed. If either is not yet adopted, this stage adds it to your bundle.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <p className="font-medium">Has your Association adopted the Emergency Stewardship Articles (6.16 through 6.21)?</p>
            <p className="text-xs text-muted-foreground mb-2">These are the articles that give the office its footing.</p>
            <RadioGroup value={articlesAdopted === null ? "" : articlesAdopted ? "yes" : "no"} onValueChange={(v) => setArticlesAdopted(v === "yes")}>
              <label className="flex items-center gap-2"><RadioGroupItem value="yes" id="art_yes" /> Yes</label>
              <label className="flex items-center gap-2"><RadioGroupItem value="no" id="art_no" /> No, add it to this bundle</label>
            </RadioGroup>
          </div>
          <div>
            <p className="font-medium">Has your Association adopted the Master Subsidiary Governance Resolution?</p>
            <p className="text-xs text-muted-foreground mb-2">This is the standing framework for how the Association governs its subsidiaries.</p>
            <RadioGroup value={masterSubAdopted === null ? "" : masterSubAdopted ? "yes" : "no"} onValueChange={(v) => setMasterSubAdopted(v === "yes")}>
              <label className="flex items-center gap-2"><RadioGroupItem value="yes" id="msg_yes" /> Yes</label>
              <label className="flex items-center gap-2"><RadioGroupItem value="no" id="msg_no" /> No, add it to this bundle</label>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Officer details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <Label>Officer full legal name</Label>
            <Input value={officerName} onChange={e => setOfficerName(e.target.value)} placeholder="Jane Elizabeth Smith" />
          </div>
          <div>
            <Label>Office title</Label>
            <Input value={officeTitle} onChange={e => setOfficeTitle(e.target.value)} placeholder="e.g., Governance Officer or Recording Secretary" />
            <p className="text-xs text-muted-foreground mt-1">Two examples: Governance Officer, Recording Secretary.</p>
          </div>
          <div>
            <p className="font-medium">Is this a new office or one that already exists?</p>
            <RadioGroup value={isNewOffice === null ? "" : isNewOffice ? "new" : "existing"} onValueChange={(v) => setIsNewOffice(v === "new")}>
              <label className="flex items-center gap-2"><RadioGroupItem value="existing" id="off_ex" /> Existing office</label>
              <label className="flex items-center gap-2"><RadioGroupItem value="new" id="off_new" /> New office (adds a Creation of an Office resolution)</label>
            </RadioGroup>
          </div>
          {isNewOffice && (
            <>
              <div>
                <Label>Office scope (semicolon separated items)</Label>
                <Textarea rows={2} value={officeScope} onChange={e => setOfficeScope(e.target.value)} placeholder="e.g., Keep the official minute book; certify Board actions; maintain the seal" />
              </div>
              <div>
                <Label>Office limitations (optional, semicolon separated items)</Label>
                <Textarea rows={2} value={officeLimits} onChange={e => setOfficeLimits(e.target.value)} placeholder="e.g., No authority to bind the Association to contracts; no signing authority on bank accounts" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Successor (optional but urged)</CardTitle>
          <CardDescription>Naming a successor now means the Company can continue without a scramble. Leave blank to skip; a Pre-Authorized Successor Manager Acceptance is added when a name is given.</CardDescription>
        </CardHeader>
        <CardContent>
          <Label>Successor full legal name</Label>
          <Input value={successorName} onChange={e => setSuccessorName(e.target.value)} placeholder="Optional" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Access map</CardTitle>
          <CardDescription>List every access this officer will receive. Names and references only, never a secret. Examples: portal role, bank signer status, a wallet by nickname, keys to the office.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Alert>
            <ShieldAlert className="w-4 h-4" />
            <AlertDescription>Never enter a password or seed phrase here. This is a map of what exists, not a vault.</AlertDescription>
          </Alert>
          {accessMap.map((row, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-start">
              <Input placeholder="What (e.g., Bank signer)" value={row.label} onChange={e => updateAccess(i, { label: e.target.value })} />
              <Input placeholder="Reference (e.g., First Wyoming operating account nickname 'Ops')" value={row.notes} onChange={e => updateAccess(i, { notes: e.target.value })} />
              <Button variant="ghost" size="sm" onClick={() => removeAccessRow(i)} disabled={accessMap.length <= 1}>Remove</Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addAccessRow}>Add a row</Button>
        </CardContent>
      </Card>

      {alreadyGenerated && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Your officer bundle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {wave3Docs.map(d => (
              <div key={d.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
                <span>{prettyKind(d.document_kind)}</span>
                <Button size="sm" variant="ghost" onClick={() => openDoc(d.storage_path, prettyKind(d.document_kind))}>Open</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={() => setStage(6)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <div className="flex gap-2">
          <Button disabled={!canGenerate || generating} onClick={handleGenerate}>
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing</> : (alreadyGenerated ? "Regenerate officer bundle" : "Generate officer bundle")}
          </Button>
          {alreadyGenerated && (
            <Button onClick={() => persist({ stage: 8 })}>Continue to Record <ArrowRight className="w-4 h-4 ml-2" /></Button>
          )}
        </div>
      </div>
    </div>
  );
}

